// /brain — the project brain, read by role. Any seat can open it; what it
// shows is whatever GET /brain/* (api/brainApi.js → shared/brainView.js)
// answers for that seat's tenant role, so the page never decides
// permissions — it renders the strata it was given:
//   viewer / member   this app's brain, the synced design, the technical
//                     reference and the data picture of their own tenant
//   admin / owner     + the kit brain, health (three liveness verdicts,
//                     self-test, seats, usage) and the runtime inbox
// Views: Overview · Knowledge · Map · Think · Technical · Data · Log · Inbox.
// Reading only; the brain is written in the repo (brain protocol) and by the
// inbox loop. Map draws the merged graph (components/BrainMap.js, shared
// with the plane); Think replays how NoVA searched it (BrainThink.js);
// Knowledge searches through GET /brain/search (shared/brainScore.js).
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { getIdToken } from '../data/authPlane';
import { kitEnv } from '../data/kitEnv';
import { useTenant } from '../data/entities';
import BrainMap, { BrainNeighbourhood } from '../components/BrainMap';
import BrainThink from '../components/BrainThink';
import { Compare } from '../components/ThinkViews';
import HowIKnew from '../components/HowIKnew';
import ContextMap from '../components/ContextMap';
import BrainHero from '../components/BrainHero';
import { mermaidCode } from '../components/Mermaid';
import { citationHref } from '../utils/citationLink';
import './Brain.css';

const API = kitEnv('UPLOAD_API', process.env.REACT_APP_UPLOAD_API || '');

async function brainApi(path, tenant, body = null) {
  const token = await getIdToken();
  const res = await fetch(`${API}${path}`, {
    ...(body ? { method: 'POST', body: JSON.stringify(body) } : {}),
    headers: {
      ...(body ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(tenant?.orgId ? { 'x-nventr-org': tenant.orgId } : {}),
      ...(tenant?.workspace ? { 'x-nventr-workspace': tenant.workspace } : {}),
    },
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) { const e = new Error(json.error || `${path}: ${res.status}`); e.status = res.status; throw e; }
  return json;
}

const VIEWS = [
  { id: 'overview', label: 'Overview' },
  { id: 'knowledge', label: 'Knowledge' },
  { id: 'map', label: 'Map' },
  { id: 'think', label: 'Think' },
  { id: 'context', label: 'Context', min: 'kit' },
  { id: 'technical', label: 'Technical' },
  { id: 'data', label: 'Data' },
  { id: 'log', label: 'Log' },
  { id: 'inbox', label: 'Inbox', min: 'inbox' },
];

const fmtDate = (v) => {
  if (!v) return '—';
  const d = typeof v === 'number' ? new Date(v) : new Date(String(v));
  return Number.isNaN(d.getTime()) ? String(v) : d.toISOString().slice(0, 10);
};
const n = (v) => (v == null ? '—' : Number(v).toLocaleString());

function Verdict({ name, v }) {
  if (!v) return null;
  return (
    <div className={`br-verdict br-${v.level || (v.ok ? 'ok' : 'warn')}`}>
      <span className="br-verdict-dot" aria-hidden="true" />
      <div>
        <div className="br-verdict-name">{name}</div>
        <div className="br-verdict-reason">{v.reason}</div>
      </div>
    </div>
  );
}

function Stat({ label, value, hint }) {
  return (
    <div className="br-stat" title={hint || ''}>
      <div className="br-stat-value">{value}</div>
      <div className="br-stat-label">{label}</div>
    </div>
  );
}

function useBrain(path, tenant, enabled = true) {
  const [state, setState] = useState({ data: null, error: null, loading: !!enabled });
  const key = `${path}|${tenant?.orgId || ''}|${tenant?.workspace || ''}`;
  const load = useCallback(() => {
    if (!enabled) return;
    setState((s) => ({ ...s, loading: true, error: null }));
    brainApi(path, tenant).then((data) => setState({ data, error: null, loading: false })).catch((e) => setState({ data: null, error: e, loading: false }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, enabled]);
  useEffect(() => { load(); }, [load]);
  return { ...state, reload: load };
}

// ---- Overview ------------------------------------------------------------------------
const TYPE_ORDER = ['system', 'decision', 'lesson', 'runbook', 'concept'];

function Overview({ o, tenant, map, ctx, setView, setPageId }) {
  const appOk = !!o.app;
  // The hero: lobes for what this seat may read, the wiki as a hemisphere of
  // types fanning out to the hub pages of the strata it can see.
  const live = o.liveness || null;
  const heat = ctx?.data?.heat || null;
  const canKit = (o.strata || []).some((s) => s.id === 'kit');
  const kitErr = o.kit?.issueCounts?.error ?? 0;
  const kitWarn = o.kit?.issueCounts?.warn ?? 0;
  const pagesAll = (o.app?.totals?.pages || 0) + (o.kit?.totals?.pages || 0);
  const lobes = [
    { id: 'app', label: "This app's brain", value: o.app?.totals?.pages ?? 0, sub: appOk ? `last entry ${fmtDate(o.app.liveness?.lastLogEntry)}` : 'no layer brain seeded', tone: appOk ? undefined : 'warn', onOpen: () => setView('knowledge') },
    { id: 'design', label: 'Design', value: o.design?.concept?.screens ?? '—', sub: o.design?.concept ? `screens in the concept · ${o.design?.stories?.count || 0} stories` : 'no App Builder concept synced', onOpen: () => setView('technical') },
    { id: 'technical', label: 'Record types', value: o.technical?.records ?? '—', sub: `${n(o.technical?.screens)} screens · ${n(o.technical?.tools)} tools for your seat`, onOpen: () => setView('technical') },
    { id: 'log', label: 'Log', value: (o.app?.totals?.logEntries || 0) + (canKit ? o.kit?.totals?.logEntries || 0 : 0), sub: canKit ? 'app + kit entries' : 'app entries', onOpen: () => setView('log') },
    ...(canKit ? [
      { id: 'kit', label: 'Kit brain', value: o.kit?.totals?.pages ?? '—', sub: `${kitErr} lint errors · ${kitWarn} warnings`, tone: kitErr ? 'err' : kitWarn ? 'warn' : 'ok', onOpen: () => setView('knowledge') },
      { id: 'corpus', label: 'Corpus', value: live?.corpusDocs ?? '—', sub: live?.corpusPending ? `${live.corpusPending} waiting to index` : 'knowledge docs indexed', tone: live?.corpusPending ? 'warn' : 'ok', onOpen: () => setView('context') },
      { id: 'inbox', label: 'Inbox', value: live?.inboxPending ?? 0, sub: 'runtime drafts for the layer repo', tone: (live?.inboxPending || 0) >= 5 ? 'warn' : undefined, onOpen: () => setView('inbox') },
      { id: 'nova', label: 'NoVA answers', value: ctx ? (heat ? heat.traces : ctx.error ? '—' : '…') : '…', sub: heat ? `${heat.distinctDocs} pages served · watch it think` : 'last 200 traces · watch it think', live: !!(heat?.until && Date.now() - heat.until < 3600000), onOpen: () => setView('think') },
    ] : []),
  ];
  const byType = {};
  for (const src of [o.app?.byType, canKit ? o.kit?.byType : null]) for (const [k, v] of Object.entries(src || {})) byType[k] = (byType[k] || 0) + v;
  const types = TYPE_ORDER.map((t) => ({ type: t, count: byType[t] || 0, onOpen: () => setView('knowledge') }));
  const hubs = useMemo(() => [...(map?.data?.nodes || [])]
    .filter((x) => TYPE_ORDER.includes(x.type))
    .sort((a, b) => (b.backlinks || 0) - (a.backlinks || 0))
    .map((x) => ({ key: x.key, slug: x.slug, docId: x.id, title: x.title, type: x.type, backlinks: x.backlinks || 0, stale: x.stale, thin: x.thin, errors: x.errors, onOpen: () => setPageId(x.id) })), [map?.data, setPageId]);
  return (
    <>
      <BrainHero
        name={o.identity?.name || 'This app'}
        sub={live?.seededAt ? `seeded ${fmtDate(live.seededAt)}${live.seededKitRef ? ` · kit ${String(live.seededKitRef).slice(0, 7)}` : ''}` : ''}
        pages={pagesAll}
        verdicts={live?.verdicts || {}}
        onOpenCenter={() => setView('map')}
        lobes={lobes}
        wiki={{ types, hubs }}
        heat={heat?.heat || null}
        note={`Reading as ${o.viewer?.role || 'viewer'}. Click the nucleus for the map; a lobe opens its view; a satellite opens that page.`}
      />
      <section className="cs-card br-card">
        <h2>{o.identity?.name || 'This app'}</h2>
        {o.identity?.goal && <p className="br-lede">{o.identity.goal}</p>}
        <p className="cs-help">
          You are reading as <strong>{o.viewer.role}</strong>{o.viewer.internal ? ' · internal' : ''} in org <code>{o.viewer.orgId}</code>{o.viewer.workspaceId ? <> · workspace <code>{o.viewer.workspaceId}</code></> : ''}.
          {' '}Strata open to this seat: {o.strata.map((s) => s.label).join(' · ')}.
        </p>
      </section>

      {o.liveness && (
        <section className="cs-card br-card">
          <h3>Is this brain alive?</h3>
          <div className="br-verdicts">
            <Verdict name="Populated" v={o.liveness.verdicts?.populated} />
            <Verdict name="Updating" v={o.liveness.verdicts?.updating} />
            <Verdict name="Current" v={o.liveness.verdicts?.current} />
          </div>
          {o.liveness.appVerdicts && (
            <p className="cs-help">This app's own brain: {['populated', 'updating', 'current'].map((k) => `${k} ${o.liveness.appVerdicts[k]?.ok ? 'ok' : 'warn'}`).join(' · ')}.</p>
          )}
          <div className="br-stats">
            <Stat label="kit pages" value={n(o.liveness.kitPages)} />
            <Stat label="app pages" value={n(o.liveness.appPages)} />
            <Stat label="knowledge docs" value={n(o.liveness.corpusDocs)} hint="knowledge collection, all audiences" />
            <Stat label="waiting to index" value={n(o.liveness.corpusPending)} />
            <Stat label="inbox drafts" value={n(o.liveness.inboxPending)} hint="brainInbox pending — drain into the layer repo" />
            <Stat label="seeded" value={fmtDate(o.liveness.seededAt)} hint={o.liveness.seededKitRef ? `kit ${String(o.liveness.seededKitRef).slice(0, 12)}` : ''} />
          </div>
        </section>
      )}

      <div className="br-grid">
        <section className="cs-card br-card">
          <h3>This app's brain</h3>
          {appOk ? (
            <>
              <div className="br-stats">
                <Stat label="pages" value={n(o.app.totals?.pages)} />
                <Stat label="log entries" value={n(o.app.totals?.logEntries)} />
                <Stat label="last entry" value={fmtDate(o.app.liveness?.lastLogEntry)} />
              </div>
              {o.app.byType && <p className="cs-help">{Object.entries(o.app.byType).map(([k, v]) => `${v} ${k}`).join(' · ')}</p>}
              {o.app.lastLog?.length > 0 && (
                <ul className="br-loglist">
                  {o.app.lastLog.map((e, i) => <li key={i}><span className="br-date">{e.date}</span> <span className="br-kind">{e.kind}</span> {e.title}</li>)}
                </ul>
              )}
            </>
          ) : (
            <p className="cs-help">No layer brain has been seeded for this app yet. A layer repo carries <code>brain/</code>; compose copies it to <code>brain/app/</code> and the seeder indexes it. Until then the kit's knowledge and the design below are what the assistant knows.</p>
          )}
        </section>

        <section className="cs-card br-card">
          <h3>What the app is made of</h3>
          <div className="br-stats">
            <Stat label="screens" value={n(o.technical?.screens)} />
            <Stat label="record types" value={n(o.technical?.records)} />
            <Stat label="tools for your seat" value={n(o.technical?.tools)} />
          </div>
          <p className="cs-help">
            {o.design?.concept ? `Design: ${o.design.concept.screens} screens in the accepted concept` : 'No App Builder concept synced'}
            {o.design?.stories?.count ? ` · ${o.design.stories.count} accepted stories` : ''}.
          </p>
        </section>

        {o.kit && (
          <section className="cs-card br-card">
            <h3>Kit brain</h3>
            <div className="br-stats">
              <Stat label="pages" value={n(o.kit.totals?.pages)} />
              <Stat label="lint errors" value={n(o.kit.issueCounts?.error)} />
              <Stat label="warnings" value={n(o.kit.issueCounts?.warn)} />
              <Stat label="last entry" value={fmtDate(o.kit.liveness?.lastLogEntry)} />
            </div>
            <p className="cs-help">{Object.entries(o.kit.byType || {}).map(([k, v]) => `${v} ${k}`).join(' · ')}{o.kit.seededKitRef ? ` · seeded from kit ${String(o.kit.seededKitRef).slice(0, 12)}` : ''}</p>
          </section>
        )}

        {o.build && (
          <section className="cs-card br-card">
            <h3>Build</h3>
            <p className="cs-help">kit <code>{o.build.kitRef || 'unknown'}</code> · data plane {o.build.dataPlane} · hosting {o.build.hosting} · <Link to="/dev">Dev Console</Link></p>
          </section>
        )}
      </div>
      {!tenant?.orgId && <p className="cs-help">No org seat yet — counts show the default org.</p>}
    </>
  );
}

// ---- Knowledge: page lists + reader ---------------------------------------------------
function PageList({ title, pages, onOpen, empty }) {
  const [q, setQ] = useState('');
  const shown = useMemo(() => {
    const t = q.trim().toLowerCase();
    return (pages || []).filter((p) => !t || `${p.title} ${(p.tags || []).join(' ')} ${p.id}`.toLowerCase().includes(t));
  }, [pages, q]);
  return (
    <section className="cs-card br-card">
      <div className="br-row">
        <h3>{title} <span className="br-count">{(pages || []).length}</span></h3>
        {(pages || []).length > 8 && <input className="cs-input br-search" placeholder="Filter…" value={q} onChange={(e) => setQ(e.target.value)} aria-label={`Filter ${title}`} />}
      </div>
      {shown.length === 0 ? <p className="cs-help">{empty || 'Nothing here yet.'}</p> : (
        <ul className="br-pages">
          {shown.map((p) => (
            <li key={p.id}>
              <button type="button" className="br-pagelink" onClick={() => onOpen(p.id)}>{p.title}</button>
              <span className="br-meta">
                {(p.tags || []).filter((t) => !['brain', 'layer-brain', 'app-sync'].includes(t)).slice(0, 3).map((t) => <span key={t} className="br-tag">{t}</span>)}
                {p.indexStatus && p.indexStatus !== 'indexed' && <span className={`br-tag br-tag-${p.indexStatus}`}>{p.indexStatus}</span>}
                {p.audience === 'internal' && <span className="br-tag">internal</span>}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// [[wikilinks]] → reader links. The page answer carries `wiki.resolve`
// (slug → knowledge id) for the stratum the page belongs to; unresolved
// links render as plain text with the slug.
function wikify(text, resolve) {
  return String(text || '').replace(/\[\[([^\]|]+)(?:\|([^\]]+))?\]\]/g, (_, slug, label) => {
    const s = slug.trim();
    const id = resolve?.[s];
    return id ? `[${label || s}](?view=knowledge&page=${encodeURIComponent(id)})` : `*${label || s}*`;
  });
}

function Reader({ id, tenant, onClose, setPageId, graph }) {
  const { data, error, loading } = useBrain(`/brain/page/${encodeURIComponent(id)}`, tenant, !!id);
  const components = useMemo(() => ({
    a: ({ href, children }) => {
      const m = /^\?view=knowledge&page=(.+)$/.exec(href || '');
      if (m) return <button type="button" className="br-pagelink br-wikilink" onClick={() => setPageId(decodeURIComponent(m[1]))}>{children}</button>;
      const external = /^https?:/i.test(href || '');
      return <a href={href} target={external ? '_blank' : undefined} rel={external ? 'noreferrer' : undefined}>{children}</a>;
    },
    code: mermaidCode,
  }), [setPageId]);
  if (!id) return null;
  const wiki = data?.wiki;
  const focusKey = wiki ? `${wiki.stratum}:${wiki.slug}` : null;
  return (
    <section className="cs-card br-card br-reader" data-nova-note={data ? `Reading brain page "${data.title}" (${id})${wiki ? `; ${wiki.backlinks?.length || 0} pages link here` : ''}` : undefined}>
      <div className="br-row">
        <h3>{data?.title || id}</h3>
        <button type="button" className="cs-btn cs-btn-ghost cs-btn-sm" onClick={onClose}>Back to list</button>
      </div>
      {loading && <p className="cs-help">Loading…</p>}
      {error && <p className="cs-help br-err">{error.status === 404 ? 'This page is not available to your seat.' : error.message}</p>}
      {data && (
        <>
          <p className="cs-help">{data.source ? <code>{data.source}</code> : null}{data.updatedAt ? ` · updated ${fmtDate(data.updatedAt)}` : ''}{data.indexStatus ? ` · ${data.indexStatus}` : ''}</p>
          {wiki?.node?.blurb ? <p className="br-lede">{wiki.node.blurb}</p> : null}
          <div className="br-md"><ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>{wikify(data.content || '_empty_', wiki?.resolve)}</ReactMarkdown></div>
          {wiki ? (
            <footer className="br-reader-foot">
              {wiki.backlinks?.length > 0 && (
                <div>
                  <h4>Linked from <span className="br-count">{wiki.backlinks.length}</span></h4>
                  <div className="br-linkrow">{wiki.backlinks.map((b) => <button key={b.slug} type="button" className="br-pagelink" onClick={() => setPageId(b.id)}>{b.title}</button>)}</div>
                </div>
              )}
              {wiki.links?.length > 0 && (
                <div>
                  <h4>Links to <span className="br-count">{wiki.links.length}</span></h4>
                  <div className="br-linkrow">{wiki.links.map((s) => (wiki.resolve?.[s] ? <button key={s} type="button" className="br-pagelink" onClick={() => setPageId(wiki.resolve[s])}>{s}</button> : <span key={s} className="br-tag">{s}</span>))}</div>
                </div>
              )}
              {graph && focusKey ? (
                <div className="br-hood">
                  <h4>Neighbourhood <span className="cs-help">— <Link to="/brain?view=map">open the map</Link></span></h4>
                  <BrainNeighbourhood graph={graph} focus={focusKey} onOpen={(node) => setPageId(node.id)} />
                </div>
              ) : null}
            </footer>
          ) : null}
        </>
      )}
    </section>
  );
}

// Server-side search over the strata the seat may read (GET /brain/search).
function useBrainSearch(q, tenant) {
  const [state, setState] = useState({ hits: [], busy: false });
  useEffect(() => {
    const query = q.trim();
    if (!query) { setState({ hits: [], busy: false }); return undefined; }
    let live = true;
    setState((s) => ({ ...s, busy: true }));
    const t = setTimeout(() => {
      brainApi(`/brain/search?q=${encodeURIComponent(query)}&top=20`, tenant)
        .then((r) => { if (live) setState({ hits: r.hits || [], busy: false }); })
        .catch(() => { if (live) setState({ hits: [], busy: false }); });
    }, 250);
    return () => { live = false; clearTimeout(t); };
  }, [q, tenant]);
  return state;
}

function SearchBox({ tenant, onOpen }) {
  const [q, setQ] = useState('');
  const { hits, busy } = useBrainSearch(q, tenant);
  return (
    <section className="cs-card br-card">
      <div className="br-row">
        <h3>Search the brain</h3>
        <input className="cs-input br-search" placeholder="e.g. deploy, voice tools, tenancy…" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search the brain" />
      </div>
      {q.trim() ? (
        busy && !hits.length ? <p className="cs-help">Searching…</p> : hits.length === 0 ? <p className="cs-help">No pages match.</p> : (
          <ul className="br-results">
            {hits.map((h) => (
              <li key={h.id}>
                <div className="br-row">
                  <button type="button" className="br-pagelink" onClick={() => onOpen(h.id)}>{h.title}</button>
                  <span className="br-meta"><span className="br-tag">{h.stratum === 'app' ? 'this app' : 'kit'}</span>{h.type ? <span className="br-tag">{h.type}</span> : null}<span className="br-count">score {h.score}</span></span>
                </div>
                {h.blurb ? <p className="cs-help">{h.blurb}</p> : null}
                {(h.evidence || []).map((line) => <p key={line} className="br-evidence">{line}</p>)}
              </li>
            ))}
          </ul>
        )
      ) : null}
    </section>
  );
}

function MapView({ tenant, setPageId, map, heat }) {
  const search = useCallback((query) => brainApi(`/brain/search?q=${encodeURIComponent(query)}&top=30`, tenant).then((r) => (r.hits || []).map((h) => `${h.stratum}:${h.slug}`)), [tenant]);
  if (map.loading && !map.data) return <p className="cs-help">Loading the map…</p>;
  if (map.error) return <p className="cs-help br-err">{map.error.message}</p>;
  const g = map.data;
  return (
    <section className="cs-card br-card">
      <h3>Map <span className="cs-help">{g?.nodes?.length ? `${g.nodes.length} pages · ${g.edges.length} wikilinks — ${g.strata.map((s) => (s === 'app' ? "this app's brain" : 'the kit brain')).join(' + ')}${heat ? ' · halos = served by NoVA lately' : ''}` : 'no graph seeded yet'}</span></h3>
      <BrainMap graph={g} heat={heat} onOpen={(node) => setPageId(node.id)} onSearch={search} />
    </section>
  );
}

// Think — watch the brain think. Ask a question (POST /voice/chat, the same
// NoVA text path, so search_brain and ask_knowledge both leave a stream) and
// replay how it was answered on the map; admins also replay any recent
// search (GET /brain/traces) or watch every search of the brain as it runs
// (GET /brain/live — NoVA turns, voice, the search box, GraphQL — polled
// every 2 s; concurrent ones are chips). [[watch-the-brain-think]]
const fmtWhen = (ms) => { const d = new Date(ms); return `${d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} ${d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}`; };
function ThinkView({ tenant, map, canKit, setPageId, heat = null }) {
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const [answer, setAnswer] = useState(null); // { reply, trace, traceId }
  const [error, setError] = useState('');
  const [cmp, setCmp] = useState(null); // { mode: 'test'|'again', busy, a, b, error } — the two tests of a finished search
  const [recent, setRecent] = useState([]);
  const [replay, setReplay] = useState(null);
  const [live, setLive] = useState(false);
  const [inflight, setInflight] = useState([]); // brainLive mirrors: searches running right now
  const [watch, setWatch] = useState(null); // Live mode's subject: { id, question, surface, at, events, done, final }
  const seenRef = useRef(0);
  const recentRef = useRef([]);
  const watchedRef = useRef(new Set()); // ids seen in flight — their finished trace updates in place, never replays
  const graph = map.data || null;

  const loadRecent = useCallback(async () => {
    if (!canKit) return;
    try { const r = await brainApi('/brain/traces?limit=30', tenant); setRecent(r.traces || []); } catch { /* the list is optional */ }
  }, [canKit, tenant]);
  useEffect(() => { loadRecent(); }, [loadRecent]);
  useEffect(() => { recentRef.current = recent; }, [recent]);

  // Live: two polls. Every 2 s the searches in flight — the newest plays as
  // it grows. Every 5 s finished traces newer than the newest seen: one
  // watched in flight updates in place (the player rests on its final
  // state); one nobody watched replays. A wall display can sit on this.
  useEffect(() => {
    if (!live || !canKit) { setInflight([]); setWatch(null); return undefined; }
    let on = true;
    seenRef.current = Math.max(seenRef.current, ...recentRef.current.map((t) => t.at || 0), 0);
    setReplay(null); setAnswer(null);
    const pollLive = async () => {
      try {
        const r = await brainApi('/brain/live', tenant);
        if (!on) return;
        const list = r.live || [];
        list.forEach((s) => watchedRef.current.add(s.id));
        setInflight(list);
        // The list is newest-activity first. Follow the most active search
        // unless the user pinned one (a chip); a search that went quiet
        // while another runs is not the one to watch.
        setWatch((w) => {
          const same = w && list.find((s) => s.id === w.id);
          if (same && (w.pinned || list[0].id === w.id)) return { ...same, pinned: w.pinned, done: false, final: false };
          if (list.length) return { ...list[0], done: false, final: false };
          if (w && !w.done) return { ...w, done: true }; // just finished — hold until its record lands
          return w;
        });
      } catch { /* try again next tick */ }
    };
    const pollTraces = async () => {
      try {
        const r = await brainApi(`/brain/traces?limit=5&after=${seenRef.current}`, tenant);
        const fresh = (r.traces || []).filter((t) => (t.at || 0) > seenRef.current);
        if (!on || !fresh.length) return;
        seenRef.current = Math.max(...fresh.map((t) => t.at || 0));
        setRecent((prev) => [...fresh.filter((t) => !prev.some((p) => p.id === t.id)), ...prev].slice(0, 30));
        setWatch((w) => {
          const mine = w && fresh.find((t) => t.id === w.id);
          if (mine) return { ...w, events: mine.think?.events || w.events, total: mine.think?.total, ms: mine.ms, hits: mine.hits, done: true, final: true };
          if (w && !w.done) return w; // never hijack a search being watched
          const unseen = fresh.find((t) => !watchedRef.current.has(t.id));
          return unseen ? { id: unseen.id, question: unseen.question, surface: unseen.surface, at: unseen.at, ms: unseen.ms, hits: unseen.hits, events: unseen.think?.events || [], total: unseen.think?.total, done: true, final: true } : w;
        });
      } catch { /* try again next tick */ }
    };
    pollLive();
    const a = setInterval(pollLive, 2000);
    const b = setInterval(pollTraces, 5000);
    return () => { on = false; clearInterval(a); clearInterval(b); };
  }, [live, canKit, tenant]);

  const ask = async (e) => {
    e?.preventDefault();
    const text = q.trim();
    if (!text || busy) return;
    setBusy(true); setError(''); setAnswer(null); setReplay(null);
    try {
      const token = await getIdToken();
      const res = await fetch(`${API}/voice/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}), ...(tenant?.orgId ? { 'x-nventr-org': tenant.orgId } : {}) },
        body: JSON.stringify({ messages: [{ role: 'user', content: text }], page: '/brain?view=think' }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `chat failed (${res.status})`);
      setAnswer({ reply: data.reply || '', trace: data.trace || null, traceId: data.traceId || null });
      loadRecent();
    } catch (err) {
      setError(err.message || 'chat failed');
    } finally {
      setBusy(false);
    }
  };

  const hrefFor = (docId) => citationHref({ docId });
  // Our own answer (its full trace) wins over the mirror of the same search
  // coming round through brainLive, under the same key — no restart.
  const ownAsk = !!answer?.trace && (!watch || (answer.traceId && watch.id === answer.traceId));
  const playing = ownAsk ? answer.trace.think?.events || [] : watch ? watch.events || [] : replay ? replay.think?.events || [] : [];
  const showLive = !ownAsk && !!watch && !watch.done;
  const playerKey = watch ? `live-${watch.id}` : replay && !ownAsk ? replay.id : 'ask';

  // The two tests of a finished search (POST /brain/think/again, admin+):
  // "test it" re-asks without the pages the answer cited — did the
  // citations carry the answer?; "ask again today" re-asks as is — what has
  // the brain learned since? Both compare the recorded stream with the new one.
  const subject = canKit && !showLive && playing.length ? { question: ownAsk ? q.trim() || answer?.trace?.variants?.[0] : watch ? watch.question : replay?.question, events: playing, reply: ownAsk ? answer?.reply : null } : null;
  const citedNow = [...(subject?.events || [])].reverse().find((e) => e.type === 'answer.done')?.citations || [];
  useEffect(() => { setCmp(null); }, [playerKey]);
  const runTest = async (mode) => {
    if (!subject?.question) return;
    const withhold = mode === 'test' ? citedNow : [];
    setCmp({ mode, busy: true, a: null, b: null, error: '' });
    try {
      const r = await brainApi('/brain/think/again', tenant, { question: subject.question, withhold });
      const label = (d) => new Date(d || Date.now()).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
      setCmp({
        mode, busy: false, error: '',
        a: { label: mode === 'test' ? 'as answered' : `recorded ${label(replay?.at || watch?.at)}`, events: subject.events, reply: subject.reply, citations: citedNow },
        b: { label: mode === 'test' ? 'without the cited pages' : 'today', events: r.trace?.think?.events || [], reply: r.reply, citations: r.citations, withheld: r.withheld || [] },
      });
      loadRecent();
    } catch (err) {
      setCmp({ mode, busy: false, a: null, b: null, error: err.message || 'failed' });
    }
  };
  return (
    <>
      <section className="cs-card br-card" data-nova-note="Think: ask a question and watch how the brain searched for the answer, replay a recent search, or watch every search live">
        <h3>Think <span className="cs-help">watch the brain search — pages light up as they are found, kept, read and cited</span></h3>
        <form className="br-think-ask" onSubmit={ask}>
          <input className="cs-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ask the brain something…" aria-label="Question" disabled={busy} />
          <button type="submit" className="cs-btn cs-btn-primary" disabled={busy || !q.trim()}>{busy ? 'Thinking…' : 'Ask'}</button>
          {canKit ? <label className="br-think-live"><input type="checkbox" checked={live} onChange={(e) => setLive(e.target.checked)} /> Live — watch every search of the brain as it runs</label> : null}
        </form>
        {error ? <p className="cs-help br-err">{error}</p> : null}
        {live && canKit ? (
          <p className="cs-help br-think-replaying">
            {inflight.length ? (
              <>
                <span className="cs-dot live" /> {inflight.length === 1 ? '1 search' : `${inflight.length} searches`} in flight:
                {inflight.map((s) => (
                  <button key={s.id} type="button" className={`cs-btn cs-btn-sm ${watch?.id === s.id ? 'cs-btn-primary' : 'cs-btn-ghost'}`} onClick={() => setWatch({ ...s, pinned: true, done: false, final: false })} title={s.surface || ''}>
                    {s.question ? `“${s.question.slice(0, 48)}${s.question.length > 48 ? '…' : ''}”` : s.surface || 'a search'} · {s.total || s.events?.length || 0}
                  </button>
                ))}
              </>
            ) : watch ? (
              <>{watch.final ? 'Finished' : 'Finishing'} {watch.question ? <>“{watch.question}”</> : 'a search'}{watch.surface ? ` · ${watch.surface}` : ''}{watch.ms ? ` · ${(watch.ms / 1000).toFixed(1)}s` : ''} · waiting for the next search</>
            ) : 'Watching — nothing is searching the brain right now. Ask NoVA anywhere in the app, search the brain, or ask above.'}
          </p>
        ) : null}
        {replay && !watch ? (
          <p className="cs-help br-think-replaying">
            Replaying {replay.question ? <>“{replay.question}”</> : 'an answer'} · {fmtWhen(replay.at)}{replay.surface ? ` · ${replay.surface}` : ''}{replay.ms ? ` · ${(replay.ms / 1000).toFixed(1)}s` : ''}
            {' '}<button type="button" className="cs-btn cs-btn-sm cs-btn-ghost" onClick={() => setReplay(null)}>clear</button>
          </p>
        ) : null}
        {playing.length || showLive ? (
          <BrainThink key={playerKey} graph={graph} events={playing} live={showLive} height={480} hrefFor={hrefFor} LinkComponent={Link} onOpen={(node) => setPageId(node.id)} heat={heat} trails={canKit ? recent : null} />
        ) : busy ? (
          <p className="cs-help">Thinking… the search replays here as soon as the answer lands.</p>
        ) : (
          <div className="cs-empty">Ask a question above{canKit ? ', pick a recent search below, or turn on Live to watch every search of the brain — NoVA turns, voice, the search box, the API — as they run' : ''}. The map lights up as a search runs: faint rings are candidates, teal is kept for the answer, white is cited, amber is a page NoVA opened. Switch views (sonar, curve, race, timing, ribbons, tape, score{canKit ? ', heat, trails' : ''}) with the picker or keys 1–9.</div>
        )}
        {subject?.question ? (
          <div className="br-think-tests" data-nova-note="Test a finished search: re-ask without its cited pages (were they load-bearing?) or re-ask today (what changed?)">
            <button type="button" className="cs-btn cs-btn-sm" disabled={cmp?.busy || !citedNow.length} onClick={() => runTest('test')} title={citedNow.length ? `Ask again with the ${citedNow.length} cited page${citedNow.length === 1 ? '' : 's'} withheld` : 'This search cited nothing to withhold'}>{cmp?.busy && cmp.mode === 'test' ? 'Testing…' : 'Test it — withhold what it cited'}</button>
            <button type="button" className="cs-btn cs-btn-sm cs-btn-ghost" disabled={cmp?.busy} onClick={() => runTest('again')} title="Ask the same question against today's corpus">{cmp?.busy && cmp.mode === 'again' ? 'Asking…' : 'Ask again today'}</button>
            <span className="cs-help">like a backtest on unseen data: an answer whose citations were load-bearing loses them when those pages are withheld</span>
          </div>
        ) : null}
        {cmp?.error ? <p className="cs-help br-err">{cmp.error}</p> : null}
        {cmp?.a && cmp?.b ? <Compare a={cmp.a} b={cmp.b} mode={cmp.mode} hrefFor={hrefFor} LinkComponent={Link} /> : null}
        {answer && (ownAsk || !watch) ? (
          <div className="br-think-answer br-md">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{answer.reply}</ReactMarkdown>
            {answer.trace ? <HowIKnew trace={answer.trace} LinkComponent={Link} hrefFor={(h) => citationHref(h)} graph={graph} /> : null}
          </div>
        ) : null}
      </section>
      {canKit ? (
        <section className="cs-card br-card">
          <h3>Recent searches <span className="cs-help">{recent.length ? `${recent.length} with a think stream — NoVA, voice, the search box, the API — click to replay` : 'nothing recorded yet'}</span></h3>
          <ul className="br-pages br-think-recent">
            {recent.map((t) => (
              <li key={t.id} className={replay?.id === t.id ? 'is-active' : ''}>
                <button type="button" className="br-think-row" onClick={() => { setReplay(t); setAnswer(null); }}>
                  <span className="br-think-q">{t.question || <em>question not recorded</em>}</span>
                  <span className="br-meta">{fmtWhen(t.at)}{t.surface ? ` · ${t.surface}` : ''} · {t.think?.total || t.think?.events?.length || 0} steps · {(t.hits || []).length} sources{t.ms ? ` · ${(t.ms / 1000).toFixed(1)}s` : ''}</span>
                </button>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </>
  );
}

// The context picture (admin+): how knowledge reaches NoVA on this instance.
function ContextView({ ctx, setPageId }) {
  if (ctx.loading && !ctx.data) return <p className="cs-help">Loading the context picture…</p>;
  if (ctx.error) return <p className="cs-help br-err">{ctx.error.status === 403 ? 'Admins only.' : ctx.error.message}</p>;
  const LinkBtn = ({ to, children }) => <button type="button" className="br-pagelink" onClick={() => setPageId(decodeURIComponent(to.replace(/^.*page=/, '')))}>{children}</button>;
  return (
    <section className="cs-card br-card">
      <h3>Context <span className="cs-help">how knowledge reaches NoVA and the builders on this instance — live numbers, and what was actually served</span></h3>
      <ContextMap context={ctx.data} LinkComponent={LinkBtn} hrefFor={(x) => (x.docId ? `/brain?view=knowledge&page=${encodeURIComponent(x.docId)}` : null)} />
    </section>
  );
}

function Knowledge({ tenant, canKit, pageId, setPageId, map }) {
  const app = useBrain('/brain/pages?stratum=app', tenant);
  const design = useBrain('/brain/pages?stratum=design', tenant);
  const kit = useBrain('/brain/pages?stratum=kit', tenant, canKit);
  if (pageId) return <Reader id={pageId} tenant={tenant} onClose={() => setPageId(null)} setPageId={setPageId} graph={map.data} />;
  return (
    <>
      <SearchBox tenant={tenant} onOpen={setPageId} />
      {app.error && <p className="cs-help br-err">{app.error.message}</p>}
      <PageList title="This app's brain" pages={app.data?.pages} onOpen={setPageId} empty="No layer brain pages seeded for this app yet." />
      {app.data?.index?.length > 0 && (
        <section className="cs-card br-card">
          <h3>Index of this app's brain</h3>
          {app.data.index.map((sec) => (
            <div key={sec.title} className="br-indexsec">
              <h4>{sec.title}</h4>
              <ul>{sec.entries.map((e) => <li key={e.slug}><button type="button" className="br-pagelink" onClick={() => setPageId(`layer-brain-${slugToId(e.slug, app.data.pages)}`)}>{e.slug}</button> — {e.blurb}</li>)}</ul>
            </div>
          ))}
        </section>
      )}
      <PageList title="Design and guide" pages={design.data?.pages} onOpen={setPageId} empty="No App Builder design synced to this instance." />
      {canKit && <PageList title="Kit brain (admin)" pages={kit.data?.pages} onOpen={setPageId} empty={kit.error ? kit.error.message : 'The kit brain has not been seeded into this instance.'} />}
      {canKit && kit.data?.issues?.length > 0 && (
        <section className="cs-card br-card">
          <h3>Kit brain lint <span className="br-count">{kit.data.issues.length}</span></h3>
          <ul className="br-issues">
            {kit.data.issues.slice(0, 40).map((i, k) => <li key={k} className={`br-issue br-${i.severity === 'error' ? 'down' : 'warn'}`}><code>{i.page}</code> {i.kind}: {i.detail || i.message || ''}</li>)}
          </ul>
        </section>
      )}
    </>
  );
}

// Index entries carry wiki slugs; knowledge ids are layer-brain-<folder>-<slug>.
function slugToId(slug, pages) {
  const hit = (pages || []).find((p) => p.id.endsWith(`-${slug}`));
  return hit ? hit.id.replace(/^layer-brain-/, '') : slug;
}

// ---- Technical reference ---------------------------------------------------------------
function Technical({ tenant }) {
  const { data: t, error, loading } = useBrain('/brain/pages?stratum=technical', tenant);
  const [openGuide, setOpenGuide] = useState(false);
  if (loading) return <p className="cs-help">Loading…</p>;
  if (error) return <p className="cs-help br-err">{error.message}</p>;
  if (!t) return null;
  return (
    <>
      <section className="cs-card br-card">
        <div className="br-row">
          <h3>App guide</h3>
          <button type="button" className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => setOpenGuide((v) => !v)}>{openGuide ? 'Hide' : 'Show'}</button>
        </div>
        <p className="cs-help">What the assistant is told about this app: screens, records and how it helps.</p>
        {openGuide && <div className="br-md"><ReactMarkdown remarkPlugins={[remarkGfm]}>{t.guide}</ReactMarkdown></div>}
      </section>
      <section className="cs-card br-card">
        <h3>Screens <span className="br-count">{t.screens.length}</span></h3>
        <ul className="br-pages">{t.screens.map((s) => <li key={s.path}><Link to={s.path.includes(':') ? '#' : s.path}>{s.label}</Link> <span className="br-meta"><code>{s.path}</code>{s.auth ? <span className="br-tag">signed in</span> : null}</span></li>)}</ul>
      </section>
      <section className="cs-card br-card">
        <h3>Records <span className="br-count">{t.records.length}</span></h3>
        <table className="br-table">
          <thead><tr><th>Record</th><th>Collection</th><th>Scope</th><th>Fields</th>{t.records[0]?.access ? <th>Access</th> : null}</tr></thead>
          <tbody>
            {t.records.map((r) => (
              <tr key={r.entity}>
                <td>{r.label}{r.parent ? <span className="br-meta"> under {r.parent}</span> : null}</td>
                <td><code>{r.collection}</code></td>
                <td>{r.scope}</td>
                <td className="br-fields">{r.fields.map((f) => f.name).join(', ')}</td>
                {r.access ? <td className="br-fields">{Object.entries(r.access).filter(([k]) => ['read', 'create', 'update', 'delete'].includes(k)).map(([k, v]) => `${k}:${v}`).join(' ')}</td> : null}
              </tr>
            ))}
          </tbody>
        </table>
      </section>
      <section className="cs-card br-card">
        <h3>Tools your seat can use <span className="br-count">{t.tools.length}</span></h3>
        <p className="cs-help">The agentOS catalog as seen by a {t.identity ? '' : ''}seat with your role — the same list the assistant, the voice agent and MCP clients get.</p>
        <ul className="br-tools">{t.tools.map((x) => <li key={x.name}><code>{x.name}</code> <span className="br-meta">{x.description}</span></li>)}</ul>
      </section>
      {t.build && (
        <section className="cs-card br-card">
          <h3>Build</h3>
          <p className="cs-help">kit <code>{t.build.kitRef || 'unknown'}</code> · version {t.build.version || '—'} · data plane {t.build.dataPlane} · hosting {t.build.hosting}</p>
        </section>
      )}
    </>
  );
}

// ---- Data picture -------------------------------------------------------------------------
function Data({ tenant }) {
  const { data, error, loading } = useBrain('/brain/pages?stratum=data', tenant);
  if (loading) return <p className="cs-help">Loading…</p>;
  if (error) return <p className="cs-help br-err">{error.message}</p>;
  if (!data) return null;
  const counted = data.records.filter((r) => r.count != null);
  const rest = data.records.filter((r) => r.count == null);
  return (
    <>
      <section className="cs-card br-card">
        <h3>Your tenant's data</h3>
        <p className="cs-help">Counts only — org <code>{data.tenant.orgId}</code>{data.tenant.workspaceId ? <> · workspace <code>{data.tenant.workspaceId}</code></> : ' · all workspaces'}. Switch workspace in the navbar to narrow the picture. Rows live on their own screens under the generated rules.</p>
        <div className="br-stats">{counted.map((r) => <Stat key={r.entity} label={r.label} value={n(r.count)} hint={r.collection} />)}</div>
        {counted.length === 0 && <p className="cs-help">Nothing counted yet.</p>}
      </section>
      {rest.length > 0 && (
        <section className="cs-card br-card">
          <h3>Not counted here</h3>
          <ul className="br-pages">{rest.map((r) => <li key={r.entity}>{r.label} <span className="br-meta">{r.note || 'n/a'}</span></li>)}</ul>
        </section>
      )}
    </>
  );
}

// ---- Log + health (admin) ----------------------------------------------------------------
function LogView({ tenant, canKit }) {
  const app = useBrain('/brain/pages?stratum=app', tenant);
  const kit = useBrain('/brain/pages?stratum=kit', tenant, canKit);
  const health = useBrain('/brain/pages?stratum=health', tenant, canKit);
  const h = health.data;
  return (
    <>
      <section className="cs-card br-card">
        <h3>This app's engineering log</h3>
        {app.data?.log?.length ? (
          <ul className="br-loglist">{app.data.log.map((e, i) => <li key={i}><span className="br-date">{e.date}</span> <span className="br-kind">{e.kind}</span> {e.title}</li>)}</ul>
        ) : <p className="cs-help">No entries yet — the layer repo's <code>brain/log.md</code> is empty or not seeded.</p>}
      </section>
      {canKit && h && (
        <section className="cs-card br-card">
          <h3>Health</h3>
          <div className="br-verdicts">
            <Verdict name="Populated" v={h.liveness?.verdicts?.populated} />
            <Verdict name="Updating" v={h.liveness?.verdicts?.updating} />
            <Verdict name="Current" v={h.liveness?.verdicts?.current} />
          </div>
          <div className="br-stats">
            <Stat label="self-test" value={h.selfTest ? `${h.selfTest.passed}/${h.selfTest.total}` : '—'} hint={h.selfTest ? `run ${fmtDate(h.selfTest.at)}` : 'no run yet'} />
            <Stat label="members" value={n(h.seats?.members)} />
            <Stat label="pending seats" value={n(h.seats?.pending)} />
            <Stat label="workspaces" value={n(h.seats?.workspaces)} />
            <Stat label="active users" value={n(h.usage?.activeUsers)} hint={h.usage?.period || ''} />
            <Stat label="index errors" value={n(h.liveness?.corpusErrors)} />
          </div>
          {h.selfTest?.failing?.length > 0 && (
            <ul className="br-issues">{h.selfTest.failing.map((f) => <li key={f.id} className="br-issue br-down"><code>{f.id}</code> {f.error}</li>)}</ul>
          )}
          {h.usage?.counters && Object.keys(h.usage.counters).length > 0 && (
            <p className="cs-help">Usage {h.usage.period}: {Object.entries(h.usage.counters).map(([k, v]) => `${k} ${n(v)}`).join(' · ')}</p>
          )}
        </section>
      )}
      {canKit && (
        <section className="cs-card br-card">
          <h3>Kit session log</h3>
          {kit.data?.log?.length ? (
            <ul className="br-loglist">{kit.data.log.map((e, i) => <li key={i}><span className="br-date">{e.date}</span> <span className="br-kind">{e.kind}</span> {e.title}</li>)}</ul>
          ) : <p className="cs-help">{kit.error ? kit.error.message : 'Kit brain not seeded.'}</p>}
        </section>
      )}
    </>
  );
}

function Inbox({ tenant }) {
  const { data, error, loading } = useBrain('/brain/pages?stratum=inbox', tenant);
  if (loading) return <p className="cs-help">Loading…</p>;
  if (error) return <p className="cs-help br-err">{error.status === 403 ? 'The inbox is for org admins.' : error.message}</p>;
  if (!data) return null;
  return (
    <section className="cs-card br-card">
      <h3>Runtime inbox <span className="br-count">{data.pending}</span></h3>
      <p className="cs-help">Drafts this instance filed for its own brain — self-test flips and notes from the control plane. Drain them into the layer repo with <code>{data.drain}</code>, then run the ingest skill.</p>
      {data.notes.length === 0 ? <p className="cs-help">Nothing waiting.</p> : (
        <ul className="br-notes">
          {data.notes.map((x) => (
            <li key={x.id}>
              <div className="br-row"><strong>{x.title}</strong><span className="br-meta"><span className="br-tag">{x.kind}</span> {fmtDate(x.createdAt)}</span></div>
              {x.related?.length > 0 && <div className="br-meta">related: {x.related.map((r) => r.slug || r).join(', ')}</div>}
              {x.body && <pre className="br-body">{x.body}</pre>}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// ---- Page -----------------------------------------------------------------------------
export default function Brain() {
  const tenant = useTenant();
  const [params, setParams] = useSearchParams();
  const view = params.get('view') || 'overview';
  const pageId = params.get('page');
  const setView = (v) => setParams(v === 'overview' ? {} : { view: v });
  const setPageId = (id) => setParams(id ? { view: 'knowledge', page: id } : { view: 'knowledge' });
  const overview = useBrain('/brain/overview', tenant);
  const map = useBrain('/brain/pages?stratum=map', tenant);
  const o = overview.data;
  const canCtx = (o?.strata || []).some((s) => s.id === 'kit');
  const ctx = useBrain('/brain/context', tenant, canCtx && (view === 'context' || view === 'map' || view === 'overview' || view === 'think'));
  const strata = new Set((o?.strata || []).map((s) => s.id));
  const canKit = strata.has('kit');
  const views = VIEWS.filter((v) => !v.min || strata.has(v.min));

  return (
    <div className="cs-page br-page" data-nova-note={`Brain page, view ${view}${pageId ? `, reading ${pageId}` : ''}; strata visible: ${[...strata].join(', ') || 'none yet'}. Views: ${views.map((v) => v.label).join(', ')}. The assistant can search these pages with search_brain and open one with navigate_app("/brain?view=knowledge&page=<id>").`}>
      <div className="cs-page-head">
        <div>
          <h1>Brain</h1>
          <p className="cs-help">Everything this project knows about itself, filtered by your seat.</p>
        </div>
        <button type="button" className="cs-btn cs-btn-ghost cs-btn-sm" onClick={overview.reload}>Refresh</button>
      </div>
      <nav className="br-tabs" aria-label="Brain views">
        {views.map((v) => <button key={v.id} type="button" className={`br-tab${view === v.id ? ' br-tab-on' : ''}`} onClick={() => setView(v.id)}>{v.label}</button>)}
      </nav>
      {overview.loading && !o && <p className="cs-help">Loading…</p>}
      {overview.error && <p className="cs-help br-err">{overview.error.status === 403 ? 'You need a seat in this org to read its brain.' : overview.error.message}</p>}
      {o && view === 'overview' && <Overview o={o} tenant={tenant} map={map} ctx={ctx} setView={setView} setPageId={setPageId} />}
      {o && view === 'knowledge' && <Knowledge tenant={tenant} canKit={canKit} pageId={pageId} setPageId={setPageId} map={map} />}
      {o && view === 'map' && <MapView tenant={tenant} setPageId={setPageId} map={map} heat={ctx.data?.heat?.heat || null} />}
      {o && view === 'think' && <ThinkView tenant={tenant} map={map} canKit={canKit} setPageId={setPageId} heat={ctx.data?.heat?.heat || null} />}
      {o && view === 'context' && canCtx && <ContextView ctx={ctx} setPageId={setPageId} />}
      {o && view === 'technical' && <Technical tenant={tenant} />}
      {o && view === 'data' && <Data tenant={tenant} />}
      {o && view === 'log' && <LogView tenant={tenant} canKit={canKit} />}
      {o && view === 'inbox' && canKit && <Inbox tenant={tenant} />}
    </div>
  );
}
